import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { apiClient } from 'lib/api';
import type { QueryConfig } from 'lib/react-query';
import queryKeys from './queryKeys';
import type { TwitterCampaignEntry } from './types';

export interface GetTwitterCampaignsParams {
  accountId: string;
}

export const getTwitterCampaigns = async (
  { params }: { params: GetTwitterCampaignsParams },
  signal?: AbortSignal,
): Promise<TwitterCampaignEntry[]> => {
  const res = await apiClient.get<TwitterCampaignEntry[]>(
    `${queryKeys.campaigns}/${params.accountId}/`,
    { signal },
  );

  if (isAxiosError(res)) {
    const error = new Error('Get X Campaigns Failed');
    error.cause = res.response?.data;
    return Promise.reject(error);
  }

  return res.data;
};

interface UseTwitterCampaignsOptions<TData> {
  params: GetTwitterCampaignsParams;
  config?: QueryConfig<typeof getTwitterCampaigns, TData>;
}

export const useTwitterCampaigns = <TData = TwitterCampaignEntry[]>({
  params,
  config,
}: UseTwitterCampaignsOptions<TData>) =>
  useQuery({
    staleTime: 0,
    refetchOnMount: true,
    retry: false,
    ...config,
    enabled: (config?.enabled ?? true) && !!params.accountId,
    queryFn: ({ signal }) => getTwitterCampaigns({ params }, signal),
    queryKey: [queryKeys.campaigns, params.accountId],
  });
